import { Localized } from "@fluent/react";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, DialogActions, DialogContent, DialogTitle, IconButton, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { CancelButton } from "../../../app/components/CancelButton";
import { DeleteButton } from "../../../app/components/DeleteButton";
import { useAppDispatch } from "../../../app/hooks";
import { deleteSupplier } from "../suppliersSlice";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { AppId } from "../../../app/appTypes";

interface DeleteSupplierDialogProps {
    supplierId: AppId;
    onClose: () => void;
    onSuccess: () => void;
}

export const DeleteSupplierDialog = ({ supplierId, onClose, onSuccess }: DeleteSupplierDialogProps) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    const handleConfirm = useCallback(async () => {
        try {
            setLoading(true);
            const result = await dispatch(deleteSupplier(supplierId));
            if(deleteSupplier.fulfilled.match(result)) {
                dispatch(showSuccess("deleted"));
                onSuccess();
                window.location.href = "/supplier";
            } else {
                dispatch(showError(result.error.code));
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setLoading(false);
        }
    }, [supplierId, dispatch, onSuccess]);

    return (
        <>
            <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Localized id="supplier-confirm-delete">_Confirm deletion</Localized>
                <IconButton
                    onClick={onClose}
                    disabled={loading}
                    size="small"
                    sx={{ position: "absolute", right: 12, top: 12 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 3, px: 3 }}>
                <Stack spacing={2}>
                    <Alert severity="warning">
                        <Localized id="supplier-confirm-delete-desc">
                            _Are you sure you want to permanently delete this supplier? This action cannot be undone.
                        </Localized>
                    </Alert>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <DeleteButton loading={loading} onClick={handleConfirm} />
                <CancelButton loading={loading} onClick={onClose} />
            </DialogActions>
        </>
    );
};
